import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api.js";
import { invalidateActiveMe } from "../lib/use-active-org.js";

/**
 * Team edit page.
 *
 * Any coach assigned to the active team (and naturally the org owner) can
 * rename the team and edit its league. The server enforces the rule via
 * the `team_coach` link.
 *
 * The route always edits the *currently active team* — switching teams
 * happens from the gear / hamburger menu in the header.
 */

interface TeamData {
  id: string;
  name: string;
  league: string | null;
}
interface MeResponse {
  activeTeam: TeamData | null;
  organization: { id: string; role: string } | null;
}
interface PatchResponse {
  team: TeamData;
}

export function TeamPage() {
  const qc = useQueryClient();

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => apiFetch<MeResponse>("/api/v1/me"),
  });

  const team = me.data?.activeTeam ?? null;

  const patch = useMutation({
    mutationFn: (body: { name?: string; league?: string | null }) => {
      if (!team) throw new Error("no_active_team");
      return apiFetch<PatchResponse>(`/api/v1/teams/${team.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
    },
    onSuccess: async () => {
      invalidateActiveMe();
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["me"] }),
        qc.invalidateQueries({ queryKey: ["teams"] }),
      ]);
    },
  });

  return (
    <div className="flex flex-col gap-4">
      <TeamCard
        team={team}
        loading={me.isLoading}
        saving={patch.isPending}
        onSave={(body) => patch.mutateAsync(body)}
      />
    </div>
  );
}

function TeamCard({
  team,
  loading,
  saving,
  onSave,
}: {
  team: TeamData | null;
  loading: boolean;
  saving: boolean;
  onSave: (patch: { name?: string; league?: string | null }) => Promise<unknown>;
}) {
  const [name, setName] = useState("");
  const [league, setLeague] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (team && !hydrated) {
      setName(team.name ?? "");
      setLeague(team.league ?? "");
      setHydrated(true);
    }
  }, [team, hydrated]);

  const trimmedName = name.trim();
  const trimmedLeague = league.trim();
  const nameDirty = !!team && trimmedName !== (team.name ?? "").trim() && trimmedName.length > 0;
  const leagueDirty = !!team && trimmedLeague !== (team.league ?? "").trim();
  const dirty = nameDirty || leagueDirty;

  return (
    <div
      className="rounded-2xl border p-5"
      style={{
        background: "var(--color-surface)",
        borderColor: "rgba(255,255,255,0.08)",
      }}
    >
      <h2 className="text-[15px] font-semibold" style={{ color: "#f4f5f3" }}>
        Team
      </h2>
      <p className="mt-1 text-[12px]" style={{ color: "var(--color-on-surface-variant)" }}>
        Bearbeite Namen und Liga des aktuell aktiven Teams.
      </p>

      {!team && !loading && (
        <p className="mt-6 text-[13px]" style={{ color: "var(--color-on-surface-variant)" }}>
          Kein aktives Team. Wähle zunächst ein Team über das Menü.
        </p>
      )}

      {team && (
        <>
          <div className="mt-6">
            <label
              htmlFor="team-name"
              className="text-[11px] uppercase tracking-[0.14em]"
              style={{ color: "var(--color-on-surface-variant)" }}
            >
              Name
            </label>
            <input
              id="team-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={loading ? "…" : "z. B. U17"}
              maxLength={120}
              className="mt-2 w-full rounded-xl border bg-transparent px-3.5 py-2.5 text-[15px] outline-none transition-colors focus:border-pitch-lime"
              style={{
                borderColor: "rgba(255,255,255,0.12)",
                color: "#f4f5f3",
              }}
            />
          </div>

          <div className="mt-4">
            <label
              htmlFor="team-league"
              className="text-[11px] uppercase tracking-[0.14em]"
              style={{ color: "var(--color-on-surface-variant)" }}
            >
              Liga
            </label>
            <input
              id="team-league"
              type="text"
              value={league}
              onChange={(e) => setLeague(e.target.value)}
              placeholder={loading ? "…" : "z. B. Kreisliga A"}
              maxLength={120}
              className="mt-2 w-full rounded-xl border bg-transparent px-3.5 py-2.5 text-[15px] outline-none transition-colors focus:border-pitch-lime"
              style={{
                borderColor: "rgba(255,255,255,0.12)",
                color: "#f4f5f3",
              }}
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              disabled={!dirty || saving}
              onClick={() => {
                setMsg(null);
                const body: { name?: string; league?: string | null } = {};
                if (nameDirty) body.name = trimmedName;
                if (leagueDirty) body.league = trimmedLeague.length > 0 ? trimmedLeague : null;
                void onSave(body)
                  .then(() => setMsg({ kind: "ok", text: "Team gespeichert." }))
                  .catch(() =>
                    setMsg({
                      kind: "err",
                      text: "Speichern fehlgeschlagen.",
                    }),
                  );
              }}
              className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-[13px] font-semibold transition-colors disabled:opacity-50"
              style={{
                background: "var(--color-pitch-lime)",
                color: "#1d3700",
              }}
            >
              {saving ? <Loader2 className="size-4 animate-spin" /> : null}
              Speichern
            </button>
          </div>

          {msg && (
            <p
              className="mt-3 text-[12px]"
              style={{
                color:
                  msg.kind === "ok" ? "var(--color-pitch-lime)" : "var(--color-danger, #ff7676)",
              }}
            >
              {msg.text}
            </p>
          )}
        </>
      )}
    </div>
  );
}
